import React, { useEffect } from 'react';

import { matchPath } from 'react-router-dom';

import { bemCssModules } from '@utils';
import { useNotifications, useLocation, useNavigate } from '@utils/hooks';
import { LocationStateType } from '@utils/types';

import ChangeEmailVerifyEmail from '@containers/Verification/ChangeEmailVerifyEmail';
import ChangeEmailVerifyOTP from '@containers/Verification/ChangeEmailVerifyOTP';
import ChangeEmailVerifyOldEmail from '@containers/Verification/ChangeEmailVerifyOldEmail';
import ChangeEmailVerifyPhone from '@containers/Verification/ChangeEmailVerifyPhone';
import ChangePasswordVerifyEmail from '@containers/Verification/ChangePasswordVerifyEmail';
import ChangePasswordVerifyOTP from '@containers/Verification/ChangePasswordVerifyOTP';
import ChangePasswordVerifyPhone from '@containers/Verification/ChangePasswordVerifyPhone';
import ChangePhoneVerifyEmail from '@containers/Verification/ChangePhoneVerifyEmail';
import ChangePhoneVerifyOTP from '@containers/Verification/ChangePhoneVerifyOTP';
import ChangePhoneVerifyPhone from '@containers/Verification/ChangePhoneVerifyPhone';
import SellVerifyOTP from '@containers/Verification/SellVerifyOTP';
import ToggleStrongSecurityOTP from '@containers/Verification/ToggleStrongSecurityOTP';
import WithdrawVerify2FA from '@containers/Verification/WithdrawVerify2FA';
import WithdrawVerifyEmail from '@containers/Verification/WithdrawVerifyEmail';
import LoginLayout from '@layouts/LoginLayout';

import style from './Verification.scss';

const cn = bemCssModules(style, 'Verification');

export type ComponentsType = {
  [key in LocationStateType['verifications']['flow']]: React.ReactElement;
};

export const Verifications: CustomFC = ({ className }) => {
  const { successEditSettingsNotification, setNewPasswordNotification } = useNotifications();

  const navigate = useNavigate();
  const location = useLocation<LocationStateType['verifications']>();
  const { state } = location;
  const { flow, password, strongPassword, nextPage, rate, value, iban } = state || {};

  const onSuccess = () => {
    if (flow === 'changePasswordVerifyEmail') setNewPasswordNotification();
    navigate<'accounts'>(nextPage, { state: { iban } });
    const isAccountPage = matchPath(CONST.nav.profile, nextPage);
    if (isAccountPage) successEditSettingsNotification();
    else navigate(nextPage);
  };

  useEffect(() => {
    if (!flow) navigate(CONST.nav.root);
  }, []);

  const components: ComponentsType = {
    changeEmailVerifyPhone: (
      <ChangeEmailVerifyPhone nextPage={nextPage} email={value} onSuccess={onSuccess} />
    ),
    changeEmailVerifyOldEmail: <ChangeEmailVerifyOldEmail onSuccess={onSuccess} />,
    changeEmailVerifyEmail: (
      <ChangeEmailVerifyEmail email={value} strongPassword={strongPassword} onSuccess={onSuccess} />
    ),
    changePhoneVerifyEmail: (
      <ChangePhoneVerifyEmail nextPage={nextPage} phone={value} onSuccess={onSuccess} />
    ),
    changePhoneVerifyPhone: (
      <ChangePhoneVerifyPhone onSuccess={onSuccess} strongPassword={strongPassword} phone={value} />
    ),
    changePasswordVerifyPhone: <ChangePasswordVerifyPhone onSuccess={onSuccess} />,
    changePasswordVerifyEmail: (
      <ChangePasswordVerifyEmail password={password} onSuccess={onSuccess} />
    ),
    withdrawVerifyEmail: <WithdrawVerifyEmail rate={rate} onSuccess={onSuccess} />,
    WithdrawVerify2FA: <WithdrawVerify2FA onSuccess={onSuccess} />,
    ChangeEmailVerifyOTP: <ChangeEmailVerifyOTP onSuccess={onSuccess} />,
    ChangePhoneVerifyOTP: <ChangePhoneVerifyOTP onSuccess={onSuccess} />,
    ToggleStrongSecurityOTP: <ToggleStrongSecurityOTP onSuccess={onSuccess} />,
    SellVerifyOTP: <SellVerifyOTP onSuccess={onSuccess} />,
    changePasswordVerifyOTP: <ChangePasswordVerifyOTP onSuccess={onSuccess} />,
  };

  if (!components[flow]) return null;

  return (
    <LoginLayout>
      <div className={cn(null, [className])}>{components[flow]}</div>
    </LoginLayout>
  );
};

export default Verifications;
